import { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { uniqueId } from "lodash"
import api from "~/api2"
import { SiteProps } from "~/interfaces"

interface ImageType {
  file: File | null
  url: string
  key: string
}

export const useSitePhotos = (site: SiteProps | null, showNotification: (args: any) => void) => {
  const queryClient = useQueryClient()
  const [imageStates, setImageStates] = useState<Array<ImageType>>([])

  const fetchImages = async () => {
    if (!site) throw new Error("No site provided")
    const response = await api.Sites.fetchImages(site.id)
    return response.images.map((image) => ({
      file: null,
      url: image.url,
      key: image.key,
    }))
  }

  const { data, isLoading } = useQuery({
    queryKey: ["siteImages", site?.id],
    queryFn: fetchImages,
    enabled: !!site,
  })

  useEffect(() => {
    if (data) {
      setImageStates(data)
    }
  }, [data])

  const uploadImageMutation = useMutation({
    mutationFn: (file: File) => api.Sites.uploadImage(site!.id, file),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["siteImages", site!.id] })
      setImageStates((currentStates) =>
        currentStates.map((img) =>
          img.key.startsWith("temp-key")
            ? { ...img, url: response.image.url, key: response.image.key, isLoading: false }
            : img,
        ),
      )
    },
    onError: (err: any, file: File) => {
      console.error(err)
      showNotification({
        type: "error",
        title: `Failed to upload image ${file.name}`,
        description: err.message,
      })
    },
  })

  const removeImageMutation = useMutation({
    mutationFn: (key: string) => api.Sites.removeImage(site!.id, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["siteImages", site!.id] })
    },
    onError: (err: any, key: string) => {
      console.error(err)
      showNotification({
        type: "error",
        title: "Failed to remove image",
        description: err.message,
      })
    },
  })

  const handleAdd = (files: FileList) => {
    const fileList = Array.from(files)
    const newImages = fileList.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      key: `temp-key-${uniqueId()}`,
      isLoading: true,
    }))

    setImageStates((prev) => [...prev, ...newImages])
    fileList.forEach((file) => uploadImageMutation.mutate(file))
  }

  const handleRemove = (key: string) => {
    setImageStates((prev) => prev.map((img) => (img.key === key ? { ...img, isLoading: true } : img)))
    removeImageMutation.mutate(key)
  }

  const updateImageLoadState = (key: string, isLoading: boolean) => {
    setImageStates((prev) => prev.map((img) => (img.key === key ? { ...img, isLoading } : img)))
  }

  return {
    imageStates,
    isLoading,
    handleAdd,
    handleRemove,
    updateImageLoadState,
  }
}
